import { Template, TemplateSection } from "./templateTypes";
import {
    createConditionalBranchingTemplate,
    createActiveLearningTemplate,
    defaultModels
} from "./templates";

const DETECTION_FAMILY = ["object-detection", "instance-segmentation"];
const ALL_MODEL_TYPES = [
    "object-detection",
    "instance-segmentation",
    "classification",
    "multi-label",
    "keypoint-detection"
];

const popularDetectionTemplates: Template[] = [
    {
        value: "custom",
        label: "Custom Workflow",
        displayLabel: "Build My Own",
        image: "/images/workflowTemplates/create.png",
        specification: require("./customModelSpecifications/custom.json"),
        userConfiguration: [{ id: "model", inputType: "model", modelType: ALL_MODEL_TYPES }],
        tags: ["generic"]
    },
    {
        value: "sam3-with-prompts",
        label: "SAM3 with Prompts",
        displayLabel: "SAM3 with Prompts",
        shortDescription: "Use SAM3 with text prompts for state of the art precise segmentation",
        icon: "fa-regular fa-sparkles",
        image: "/images/workflowTemplates/sam3-prompts.png",
        specification: require("./customModelSpecifications/sam3-prompts.json"),
        userConfiguration: [],
        tags: ["generic"],
        new: true
    },
    {
        value: "detect-visualize-count",
        label: "Detect, Count, and Visualize",
        shortDescription: "Count the number of detections using this model",
        icon: "far fa-diagram-project",
        image: "/images/workflowTemplates/detect-count-visualize.png",
        specification: require("./customModelSpecifications/detect-count-and-visualize.json"),
        userConfiguration: [{ id: "model", inputType: "model", modelType: DETECTION_FAMILY }],
        tags: ["generic"]
    },
    {
        value: "detect-and-classify",
        label: "Detect and Classify",
        description:
            "Run detection to locate objects, then classify each detected region to identify the specific category or attribute.",
        shortDescription: "Classify detections from this model",
        icon: "far fa-badge-check",
        image: "/images/workflowTemplates/detect-and-classify.png",
        specification: require("./customModelSpecifications/detect-and-classify.json"),
        userConfiguration: [
            { id: "detection_model", inputType: "model", modelType: DETECTION_FAMILY },
            {
                id: "classification_model",
                inputType: "model",
                modelType: ["classification", "multi-label"]
            }
        ],
        tags: ["generic"]
    },
    {
        value: "sahi",
        label: "Small Object Detection (SAHI)",
        shortDescription: "Improve small-object recall using slicing",
        icon: "far fa-border-inner",
        image: "/images/workflowTemplates/sahi-small-object-detection.png",
        specification: require("./customModelSpecifications/sahi.json"),
        userConfiguration: [{ id: "model", inputType: "model", modelType: ["object-detection"] }],
        tags: ["generic"],
        examples: [
            {
                name: "Bleachers",
                input: {
                    image: {
                        type: "url",
                        validUrl: true,
                        value: "https://media.roboflow.com/workflows/examples/bleachers.jpg"
                    }
                }
            }
        ]
    },
    {
        value: "text-recognition",
        label: "Text Recognition",
        shortDescription: "Detect and read text from images",
        icon: "far fa-text",
        image: "/images/workflowTemplates/text-recognition.png",
        specification: require("./customModelSpecifications/text-recognition.json"),
        userConfiguration: [
            {
                id: "model",
                inputType: "model",
                modelType: DETECTION_FAMILY,
                default: "text-detection-tjvsp/4" // COCO default doesn't detect text
            }
        ],
        tags: ["use-case"]
    },
    {
        value: "bg-removal",
        label: "Background Removal",
        shortDescription: "Remove backgrounds from detected objects",
        icon: "far fa-scissors",
        image: "/images/workflowTemplates/remove-background.png",
        specification: require("./customModelSpecifications/bg-removal.json"),
        userConfiguration: [{ id: "model", inputType: "model", modelType: DETECTION_FAMILY }],
        tags: ["generic"]
    }
];

const popularClassificationTemplates: Template[] = [
    {
        value: "custom",
        label: "Custom Workflow",
        displayLabel: "Build My Own",
        image: "/images/workflowTemplates/create.png",
        specification: require("./customModelSpecifications/classification-custom.json"),
        userConfiguration: [
            { id: "model", inputType: "model", modelType: ["classification", "multi-label"] }
        ],
        tags: ["generic"]
    },
    {
        value: "classify-and-conditionally-detect",
        label: "Classify and Conditionally Detect",
        shortDescription: "Classify images then conditionally run detection",
        icon: "far fa-route",
        image: "/images/workflowTemplates/classify-and-conditionally-detect.png",
        specification: require("./customModelSpecifications/classify-and-conditionally-detect.json"),
        userConfiguration: [
            {
                id: "classification_model",
                inputType: "model",
                modelType: ["classification", "multi-label"]
            },
            { id: "detection_model", inputType: "model", modelType: DETECTION_FAMILY }
        ],
        tags: ["generic"]
    },
    {
        value: "detect-and-classify",
        label: "Detect and Classify",
        image: "/images/workflowTemplates/detect-and-classify.png",
        specification: require("./customModelSpecifications/detect-and-classify.json"),
        userConfiguration: [
            { id: "detection_model", inputType: "model", modelType: DETECTION_FAMILY },
            {
                id: "classification_model",
                inputType: "model",
                modelType: ["classification", "multi-label"]
            }
        ],
        tags: ["generic"]
    }
];

const popularMultiLabelClassificationTemplates: Template[] = [
    {
        value: "custom",
        label: "Custom Workflow",
        displayLabel: "Build My Own",
        image: "/images/workflowTemplates/create.png",
        specification: require("./customModelSpecifications/multi-label-classification-custom.json"),
        userConfiguration: [
            { id: "model", inputType: "model", modelType: ["classification", "multi-label"] }
        ],
        tags: ["generic"]
    }
];

const detectionLogicTemplates: Template[] = [
    createConditionalBranchingTemplate("object-detection"),
    createActiveLearningTemplate("object-detection")
];

const classificationLogicTemplates: Template[] = [
    createConditionalBranchingTemplate("classification"),
    createActiveLearningTemplate("classification")
];

export const lineCounter: Template = {
    value: "line-counting",
    label: "Line Counter",
    description:
        "Count how many objects cross a virtual line in video streams, ideal for people counting, traffic monitoring, and production line analysis.",
    shortDescription: "Count objects crossing a line in video",
    icon: "far fa-chart-line",
    image: "/images/workflowTemplates/line-counter.png",
    specification: require("./customModelSpecifications/line-counter.json"),
    userConfiguration: [{ id: "model", inputType: "model", modelType: ["object-detection"] }],
    tags: ["generic"]
};

export const timeInZone: Template = {
    value: "time-in-zone",
    label: "Time in Zone",
    description:
        "Track how long objects remain in a designated zone, useful for dwell time analysis and occupancy monitoring.",
    shortDescription: "Track time objects spend in a zone",
    icon: "far fa-stopwatch",
    image: "/images/workflowTemplates/time-in-zone.png",
    specification: require("./customModelSpecifications/time-in-zone.json"),
    userConfiguration: [{ id: "model", inputType: "model", modelType: ["object-detection"] }],
    tags: ["generic"]
};

const videoTemplates: Template[] = [lineCounter, timeInZone];

const comingSoonTemplates: Template[] = [
    {
        value: "email-notifications",
        label: "Email Notifications",
        image: "/images/workflowTemplates/email-notifications.png",
        specification: require("./customModelSpecifications/email-notifications.json"),
        comingSoon: true,
        tags: ["generic"]
    },
    {
        value: "csv-export",
        label: "CSV Export",
        image: "/images/workflowTemplates/csv-export.png",
        specification: require("./customModelSpecifications/csv-export.json"),
        comingSoon: true,
        tags: ["generic"]
    }
];

const multiLabelClassificationComingSoonTemplates: Template[] = [
    {
        value: "email-notifications",
        label: "Email Notifications",
        image: "/images/workflowTemplates/email-notifications.png",
        specification: require("./customModelSpecifications/email-notifications.json"),
        comingSoon: true,
        tags: ["generic"]
    },
    { ...createActiveLearningTemplate("multi-label-classification"), comingSoon: true }
];

function uniqueByValue(templates: Template[]): Template[] {
    const seen = new Set<string>();
    const out: Template[] = [];
    templates.forEach((t) => {
        if (!seen.has(t.value)) {
            seen.add(t.value);
            out.push(t);
        }
    });
    return out;
}

const tutorialTemplates: Template[] = [
    {
        value: "detect-and-count-tutorial",
        label: "Detect, Count, and Visualize",
        displayLabel: "Detect, Count, and Visualize",
        shortDescription: "Step-by-step guided tutorial",
        icon: "far fa-diagram-project",
        image: "/images/workflowTemplates/detect-count-visualize.png",
        userConfiguration: [],
        tags: ["tutorial"],
        walkthroughId: "detect-and-count"
    },
    {
        value: "video-line-counter-tutorial",
        label: "Live Video Line Counter",
        displayLabel: "Live Video Line Counter",
        shortDescription: "Step-by-step guide for live video line counting",
        icon: "far fa-chart-line",
        image: "/images/workflowTemplates/line-counter.png",
        userConfiguration: [],
        tags: ["tutorial"],
        walkthroughId: "video-line-counter"
    }
];

// Unified Templates: Merge across types; each template carries its own allowed model types via userConfiguration
export const unifiedTemplateSections: TemplateSection[] = [
    {
        title: "Tutorial",
        description: "Follow a step-by-step guide to build your first workflow",
        templates: tutorialTemplates
    },
    {
        title: "Popular Templates",
        templates: uniqueByValue([
            ...popularDetectionTemplates,
            ...popularClassificationTemplates,
            ...popularMultiLabelClassificationTemplates
        ])
    },
    {
        title: "Business Logic",
        templates: uniqueByValue([...detectionLogicTemplates, ...classificationLogicTemplates])
    },
    { title: "Video Analytics", templates: uniqueByValue(videoTemplates) },
    {
        title: "Coming Soon",
        templates: uniqueByValue([
            ...comingSoonTemplates,
            ...multiLabelClassificationComingSoonTemplates
        ])
    }
];

// Backwards-compatible named exports
export const detectionTemplateSections: TemplateSection[] = unifiedTemplateSections;
export const classificationTemplateSections: TemplateSection[] = unifiedTemplateSections;
export const multiLabelClassificationTemplateSections: TemplateSection[] = unifiedTemplateSections;
